/**
 * Account Takeover prevention — per-domain, rule-based protection for the
 * Security tab. Pick a domain, switch between the Failed login / Credential
 * stuffing categories, and manage each category's rules (create from a template,
 * edit, enable/disable, delete).
 *
 * Backed by `/waf/atp/*` (Owl/Mlytics AccountTakeoverService + templates).
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldAlert, RotateCcw, Plus, Pencil, Trash2 } from 'lucide-react';
import { Container, PageHeader } from '@vnetwork/ui/layout';
import { DataTable, EmptyState, type ColumnDef } from '@vnetwork/ui/data';
import {
  Skeleton,
  Button,
  IconButton,
  Switch,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Card,
  CardBody,
  Tabs,
  TabsList,
  TabsTrigger,
  Tooltip,
  TooltipProvider,
} from '@vnetwork/ui/primitives';
import { Alert, ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import {
  useWafATPRules,
  useWafSecurityTemplates,
  useSetATPRuleEnabled,
  useDeleteATPRule,
  fmtEpoch,
  wafExtractError,
  type ATPCategory,
  type AccountTakeoverRule,
} from '@/api/waf';
import { trackFeatureAction } from '@/lib/analytics-events';
import { WafScopeBar, type WafScope } from '../analytics/WafScopeBar';
import styles from './WafAccountTakeover.module.css';

const muted = (v: React.ReactNode) => <span style={{ color: 'var(--vn-fg-muted)' }}>{v}</span>;

/** Build the create/edit form URL for a category (+ optional rule id / template). */
function formHref(serviceId: string, domain: string, category: ATPCategory, opts?: { id?: string; template?: string }) {
  const q = new URLSearchParams({ domain, category });
  if (opts?.id) q.set('id', opts.id);
  if (opts?.template) q.set('template', opts.template);
  return `/services/waf/${serviceId}/account-takeover/${opts?.id ? 'edit' : 'new'}?${q.toString()}`;
}

/** A compact one-line summary of a rule's action. */
function actionSummary(rule: AccountTakeoverRule): string {
  const a = rule.action;
  if (rule.type === 'credentialStuffing' || a.type === 'credentialStuffing') {
    return a.cc_action ? `Action: ${a.cc_action}` : '—';
  }
  const parts: string[] = [];
  if (a.req_count != null) parts.push(`${a.req_count} req`);
  if (a.fl_check_time != null) parts.push(`/ ${a.fl_check_time}s`);
  if (a.fl_action) parts.push(`→ ${a.fl_action}`);
  return parts.length ? parts.join(' ') : '—';
}

/** Inline enable/disable toggle for a rule. */
function EnableCell({ serviceId, domain, category, rule }: { serviceId: string; domain: string; category: ATPCategory; rule: AccountTakeoverRule }) {
  const { show } = useToast();
  const setEnabled = useSetATPRuleEnabled(serviceId, {
    onSuccess: (_d, vars) => { show({ tone: 'success', title: vars.enable ? 'Rule enabled' : 'Rule disabled' }); trackFeatureAction('waf', 'atp_rule', vars.enable ? 'enable' : 'disable'); },
    onError: (e) => show({ tone: 'danger', title: wafExtractError(e, 'Update failed') }),
  });
  return (
    <div onClick={(e) => e.stopPropagation()} style={{ display: 'inline-flex' }}>
      <Switch
        checked={rule.enable}
        disabled={setEnabled.isPending}
        aria-label={`Enable ${rule.name}`}
        onCheckedChange={(enable) => setEnabled.mutate({ domain, category, id: rule.id, enable })}
      />
    </div>
  );
}

/** Edit + delete actions for a rule. */
function ActionsCell({ serviceId, domain, category, rule }: { serviceId: string; domain: string; category: ATPCategory; rule: AccountTakeoverRule }) {
  const { show } = useToast();
  const navigate = useNavigate();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const del = useDeleteATPRule(serviceId, {
    onSuccess: () => { show({ tone: 'success', title: 'Rule deleted' }); trackFeatureAction('waf', 'atp_rule', 'delete'); setConfirmDelete(false); },
    onError: (e) => show({ tone: 'danger', title: wafExtractError(e, 'Delete failed') }),
  });
  return (
    <div onClick={(e) => e.stopPropagation()} style={{ display: 'inline-flex', gap: 2 }}>
      <TooltipProvider>
        <Tooltip content="Edit" side="top">
          <IconButton variant="ghost" size="sm" aria-label="Edit rule" onClick={() => navigate(formHref(serviceId, domain, category, { id: rule.id }))}><Pencil size={14} /></IconButton>
        </Tooltip>
        <Tooltip content="Delete" side="top">
          <IconButton variant="ghost" size="sm" aria-label="Delete rule" onClick={() => setConfirmDelete(true)}><Trash2 size={14} /></IconButton>
        </Tooltip>
      </TooltipProvider>
      {confirmDelete && (
        <ConfirmDialog
          open
          onOpenChange={(o) => { if (!o) setConfirmDelete(false); }}
          title="Delete rule?"
          description={`Delete the rule "${rule.name}"? This cannot be undone.`}
          confirmLabel="Delete"
          destructive
          loading={del.isPending}
          onConfirm={() => del.mutate({ domain, category, id: rule.id })}
        />
      )}
    </div>
  );
}

/** Rules table for one category. */
function RulesCard({ serviceId, domain, category }: { serviceId: string; domain: string; category: ATPCategory }) {
  const navigate = useNavigate();
  const [tpl, setTpl] = useState('');
  const templatesQ = useWafSecurityTemplates(serviceId, category);
  const templates = templatesQ.data ?? [];
  const goNew = () => navigate(formHref(serviceId, domain, category));
  const goAdd = () => navigate(formHref(serviceId, domain, category, tpl ? { template: tpl } : undefined));
  const { data, isLoading, isError, refetch } = useWafATPRules(serviceId, domain, category);
  const rules = data ?? [];

  const columns = useMemo<ColumnDef<AccountTakeoverRule, unknown>[]>(
    () => [
      { id: 'name', header: 'Name', enableSorting: false, cell: ({ row }) => <span style={{ fontWeight: 500 }}>{row.original.name}</span> },
      { id: 'enable', header: 'Enabled', enableSorting: false, cell: ({ row }) => <EnableCell serviceId={serviceId} domain={domain} category={category} rule={row.original} /> },
      { id: 'priority', header: 'Priority', enableSorting: false, cell: ({ row }) => muted(row.original.priority ?? '—') },
      { id: 'action', header: 'Action', enableSorting: false, cell: ({ row }) => muted(actionSummary(row.original)) },
      { id: 'updated', header: 'Updated', enableSorting: false, cell: ({ row }) => muted(fmtEpoch(row.original.updated_at)) },
      { id: 'actions', header: 'Actions', enableSorting: false, cell: ({ row }) => <ActionsCell serviceId={serviceId} domain={domain} category={category} rule={row.original} /> },
    ],
    [serviceId, domain, category],
  );

  return (
    <Card>
      <CardBody>
        <div className={styles.toolbar}>
          <div className={styles.spacer} />
          <div className={styles.actions}>
            <Button variant="secondary" startIcon={<RotateCcw size={15} />} onClick={() => refetch()} aria-label="Refresh rules">Refresh</Button>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--vn-space-2)' }}>
              <Select value={tpl} onValueChange={setTpl} disabled={templatesQ.isLoading}>
                <SelectTrigger aria-label="Template" style={{ width: 200, flex: '0 0 auto' }}>
                  <SelectValue placeholder={templatesQ.isLoading ? 'Loading…' : 'Blank rule'} />
                </SelectTrigger>
                <SelectContent>
                  {templates.map((t) => (
                    <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button startIcon={<Plus size={15} />} onClick={goAdd}>Add</Button>
            </div>
          </div>
        </div>

        {isLoading ? (
          <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
            {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} style={{ height: 44 }} />)}
          </div>
        ) : isError ? (
          <Alert tone="danger" title="Couldn't load rules" actions={<button onClick={() => refetch()}>Retry</button>}>
            The WAF service couldn't return the rules for this category. Try again.
          </Alert>
        ) : rules.length === 0 ? (
          <EmptyState
            icon={<ShieldAlert size={20} />}
            title="No rules yet"
            description="Create a rule (optionally from a template) to start protecting this domain."
            actions={<Button startIcon={<Plus size={15} />} onClick={goNew}>New rule</Button>}
          />
        ) : (
          <DataTable columns={columns} data={rules} getRowId={(r) => r.id} emptyState="No rules." />
        )}
      </CardBody>
    </Card>
  );
}

export function WafAccountTakeover({ serviceId }: { serviceId: string }) {
  const [scope, setScope] = useState<WafScope>({});
  const [category, setCategory] = useState<ATPCategory>('failed_login');
  const domain = scope.subdomain;
  const noSubdomain = !!scope.zone && !domain;

  return (
    <Container size="xl">
      <PageHeader
        icon={<ShieldAlert size={22} />}
        title="Account Takeover"
        description="Protect the selected subdomain from failed-login abuse and credential-stuffing attacks."
      />

      <div className={styles.scopeBar}>
        <WafScopeBar serviceId={serviceId} mode="subdomain" onChange={setScope} />
      </div>

      {noSubdomain ? (
        <EmptyState
          icon={<ShieldAlert size={20} />}
          title="No proxied subdomains"
          description="This zone has no proxied subdomain to protect. Proxy a record first."
        />
      ) : !domain ? (
        <EmptyState
          icon={<ShieldAlert size={20} />}
          title="Select a subdomain"
          description="Pick a domain and subdomain above to manage account-takeover rules."
        />
      ) : (
        <>
          <div className={styles.tabsRow}>
            <Tabs value={category} onValueChange={(v) => setCategory(v as ATPCategory)}>
              <TabsList>
                <TabsTrigger value="failed_login">Failed login</TabsTrigger>
                <TabsTrigger value="credential_stuffing">Credential stuffing</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
          <RulesCard key={category} serviceId={serviceId} domain={domain} category={category} />
        </>
      )}
    </Container>
  );
}

export default WafAccountTakeover;
